import clsx from 'clsx';
import React from 'react';

import { Icon } from '@gitbook/spine-icons';

import { Button, type ButtonProps } from '../Button';
import { Gradient } from './assets/Gradient';
import { Layout, LayoutRoot } from './Layout';

/**
 * A template for a full-screen split page with main and aside (with gradient) sections
 */
export function PageSplit(props: { children: React.ReactNode }) {
    return (
        <LayoutRoot layout="full-screen-split">
            <main className="bg-base">
                <Layout className="overflow-hidden">
                    {props.children}
                    {React.Children.count(props.children) === 1 ? <PageSplitAside /> : null}
                </Layout>
            </main>
        </LayoutRoot>
    );
}

/**
 * The main content area of the PageSplit template
 */
export function PageSplitMain(props: { children: React.ReactNode }) {
    return (
        <Layout kind="full-width">
            <section className="mx-auto grid h-screen max-w-full grid-rows-[auto_minmax(0,_1fr)_auto] [grid-template-areas:'header''body''footer']">
                {props.children}
            </section>
        </Layout>
    );
}

/**
 * Secondary area of the PageSplit template with a gradient background
 */
export function PageSplitAside(props: {
    children?: React.ReactNode;
    className?: string;
    customGradient?: React.ReactNode;
}) {
    const { children, className, customGradient } = props;

    return (
        <aside className={clsx('relative overflow-hidden max-lg:hidden', className)}>
            {customGradient ?? (
                <Gradient
                    className="absolute object-cover"
                    style={{
                        width: 1480,
                        minHeight: 980,
                        height: '100%',
                    }}
                />
            )}
            {children}
        </aside>
    );
}

export function PageSplitHeader(props: { children: React.ReactNode }) {
    return <header className="[grid-area:header]">{props.children}</header>;
}

export function PageSplitBody(props: { children: React.ReactNode; size?: 'small' | 'medium' }) {
    const { children, size = 'medium' } = props;
    return (
        <Layout
            kind="centered"
            className="overflow-y-auto [grid-area:body]"
            slotClassName="content-start pb-4 lg:content-center"
        >
            <div
                className={clsx(
                    'mx-auto flex flex-col gap-8 py-4',
                    size === 'small' && 'max-w-[22rem]',
                    size === 'medium' && 'max-w-[30rem]'
                )}
            >
                {children}
            </div>
        </Layout>
    );
}

export function PageSplitFooter(props: { children: React.ReactNode }) {
    return <footer className="[grid-area:footer]">{props.children}</footer>;
}

export function PageSplitCloseButton(props: Pick<ButtonProps, 'label' | 'href' | 'onPress'>) {
    return (
        <div className="absolute top-0 right-0 z-10 mt-4 mr-4 flex">
            <Button
                {...props}
                label={props.label ?? 'Close'}
                kind="blank"
                leadingIcon={Icon.X}
                iconOnly
                className="ml-auto"
            />
        </div>
    );
}

export function PageSplitTitle(props: { children: React.ReactNode; logo?: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-4 pt-4">
            {props.logo}
            <h2 className="text-ui-heading-large">{props.children}</h2>
        </div>
    );
}

export function PageSplitDescription(props: { children: React.ReactNode }) {
    return <>{props.children}</>;
}
